import { Tab, TabPanel } from 'components/Tabs';
import {
  selectBreakoutRoomsStatus,
  selectIsBreakoutRoom,
} from 'features/breakout-rooms/breakoutRoomsSlice';
import VideoTab from 'features/room/settings/tabs/VideoTab';
import VideoTabWithoutPermissions from 'features/room/settings/tabs/VideoTabWithoutPermissions';
import { selectVirtualBackgroundAvailable } from 'features/virtual-backgrounds/selectors';
import { useForm } from 'react-hook-form';
import SettingsTabs from 'components/SettingsTabs';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store/hooks';
import WorkspaceTab from 'features/room/settings/tabs/WorkspaceTab';
import AudioTabContainer from 'features/room/settings/tabs/AudioTabContainer';
import { selectActiveMediaDevices } from 'features/user-media/userMediaSlice';
import { selectCaptionsSettingsAvailable } from 'features/transcripts/slices/captions/captionsSlice';
import { selectDevModeEnabled } from 'features/dev-mode/devModeSlice';
import DevModeTab from 'features/room/settings/tabs/DevModeTab';
import { canExposeInternals } from 'utils/development';
import SystemStatsTab from 'features/room/settings/tabs/SystemStatsTab';
import VirtualBackground from 'features/room/settings/tabs/VirtualBackground';
import Captions from 'features/room/settings/tabs/CaptionsTab';
import React from 'react';
import { usePermissions } from 'features/permissions/usePermissions';
import { PermissionTypes } from 'features/permissions/types';
import ComponentsTab from 'features/room/settings/tabs/ComponentsTab';
import ClearContentTab from 'features/room/settings/tabs/ClearContentTab';
import SecurityTab from 'features/room/settings/tabs/SecurityTab';
import { selectE2eeEnabled } from 'features/e2ee/e2eeSlice';
import { selectIsAudioOnlyRoom } from 'features/room/roomSlice';

export interface RoomSettingsFormValues {
  videoinput: string;
  audiooutput: string;
  audioinput: string;
}

interface RoomSettingsProps {
  activeTabIndex?: number;
}

const RoomSettings = ({ activeTabIndex }: RoomSettingsProps) => {
  const { t } = useTranslation('settings');

  const activeMediaDevices = useAppSelector(selectActiveMediaDevices);
  const devModeEnabled = useAppSelector(selectDevModeEnabled);

  // @TODO handle tab form values separately once we have more features, consider removing the form if there is no fields validation
  const { control, setValue } = useForm<RoomSettingsFormValues>({
    defaultValues: {
      audioinput: activeMediaDevices.audioinput || '',
      audiooutput: activeMediaDevices.audiooutput || '',
      videoinput: activeMediaDevices.videoinput || '',
    },
  });

  const { hasPermissions } = usePermissions();
  const allowVideoBroadcast = hasPermissions(PermissionTypes.broadcastVideo);
  const allowAudioBroadcast = hasPermissions(PermissionTypes.broadcastAudio);

  const VBAvailable = useAppSelector(selectVirtualBackgroundAvailable);
  const showVB = allowVideoBroadcast && VBAvailable;

  const isAudioOnlyRoom = useAppSelector(selectIsAudioOnlyRoom);
  const showVideoTab = !isAudioOnlyRoom;

  const canControlComponents = hasPermissions(PermissionTypes.controlRoomComponents);

  const isBreakout = useAppSelector(selectIsBreakoutRoom);
  const breakoutRoomsStatus = useAppSelector(selectBreakoutRoomsStatus);

  const e2eeEnabled = useAppSelector(selectE2eeEnabled);

  const canClearContent =
    hasPermissions(PermissionTypes.clearContent) &&
    !isBreakout &&
    breakoutRoomsStatus !== 'started';

  const showCaptionSettings = useAppSelector(selectCaptionsSettingsAvailable);

  return (
    <SettingsTabs
      defaultActiveIndex={activeTabIndex || 0}
      renderPanels={(activeTab) => (
        <>
          {devModeEnabled && (
            <TabPanel
              value={activeTab}
              index={99}
              id="devmode-tabpanel"
              aria-labelledby="devmode-tab"
            >
              <DevModeTab />
            </TabPanel>
          )}
          <TabPanel
            value={activeTab}
            index={0}
            id="workspace-tabpanel"
            aria-labelledby="workspace-tab"
          >
            <WorkspaceTab />
          </TabPanel>

          {showVideoTab && (
            <TabPanel value={activeTab} index={1} id="video-tabpanel" aria-labelledby="video-tab">
              {allowVideoBroadcast ? (
                <VideoTab control={control} setValue={setValue} />
              ) : (
                <VideoTabWithoutPermissions />
              )}
            </TabPanel>
          )}
          {allowAudioBroadcast && (
            <TabPanel value={activeTab} index={2} id="audio-tabpanel" aria-labelledby="audio-tab">
              <AudioTabContainer control={control} setValue={setValue} />
            </TabPanel>
          )}
          {showVB && (
            <TabPanel value={activeTab} index={3} id="vb-tabpanel" aria-labelledby="vb-tab">
              <VirtualBackground />
            </TabPanel>
          )}
          {showCaptionSettings && (
            <TabPanel
              value={activeTab}
              index={4}
              id="captions-tabpanel"
              aria-labelledby="captions-tab"
            >
              <Captions />
            </TabPanel>
          )}
          {e2eeEnabled && (
            <TabPanel
              value={activeTab}
              index={5}
              id="security-tabpanel"
              aria-labelledby="security-tab"
            >
              <SecurityTab />
            </TabPanel>
          )}
          {canControlComponents && (
            <TabPanel
              value={activeTab}
              index={6}
              id="components-tabpanel"
              aria-labelledby="components-tab"
            >
              <ComponentsTab />
            </TabPanel>
          )}
          {canClearContent && (
            <TabPanel
              value={activeTab}
              index={7}
              id="clear-content-tabpanel"
              aria-labelledby="clear-content-tab"
            >
              <ClearContentTab />
            </TabPanel>
          )}
          {canExposeInternals() ? (
            <TabPanel value={activeTab} index={100} id="stats-tabpanel" aria-labelledby="stats-tab">
              <SystemStatsTab />
            </TabPanel>
          ) : null}
        </>
      )}
    >
      {devModeEnabled && (
        <Tab
          label={t('dev_mode.tab_label')}
          value={99}
          id="devmode-tab"
          aria-controls="devmode-tabpanel"
        />
      )}
      <Tab
        label={t('workspace.tab_label')}
        value={0}
        id="workspace-tab"
        aria-controls="workspace-tabpanel"
      />
      {showVideoTab && (
        <Tab label={t('video.tab_label')} value={1} id="video-tab" aria-controls="video-tabpanel" />
      )}
      {allowAudioBroadcast && (
        <Tab label={t('audio.tab_label')} value={2} id="audio-tab" aria-controls="audio-tabpanel" />
      )}
      {showVB && (
        <Tab label={t('virtual_bg.tab_label')} value={3} id="vb-tab" aria-controls="vb-tabpanel" />
      )}
      {showCaptionSettings && (
        <Tab
          label={t('captions.tab_label')}
          value={4}
          id="captions-tab"
          aria-controls="captions-tabpanel"
        />
      )}
      {e2eeEnabled && (
        <Tab
          label={t('security.tab_label')}
          value={5}
          id="security-tab"
          aria-controls="security-tabpanel"
        />
      )}
      {canControlComponents && (
        <Tab
          label={t('components.tab_label')}
          value={6}
          id="components-tab"
          aria-controls="components-tabpanel"
        />
      )}
      {canClearContent && (
        <Tab
          label={t('clear_content.tab_label')}
          value={7}
          id="clear-content-tab"
          aria-controls="clear-content-tabpanel"
        />
      )}
      {canExposeInternals() ? (
        <Tab
          label={t('system_stats.tab_label')}
          value={100}
          id="stats-tab"
          aria-controls="stats-tabpanel"
        />
      ) : null}
    </SettingsTabs>
  );
};

export default RoomSettings;
